import { action, flow, makeObservable, observable } from 'mobx';

import { isPrimitive } from 'src/packages/types';
import { hasValue } from 'src/packages/utils/has-value';

import type { TComboBoxView, TControlView, TMultiComboBoxView } from './types/controls-views.type';
import type { TRefQuery } from '../directories-storage-service/types';
import type { NotificationsService } from '../notifications-service';

import { mapComboboxOptionsConcatinationRule } from '../control-rules-service/mappers';
import { handleComboboxOptionsConcatinationRule } from '../control-rules-service/rule-handlers/handle-combobox-options-concatination-rule';
import {
  REF_QUERY_REG_EXP,
  type IDirectoriesStorage,
  type TStorageObject,
} from '../directories-storage-service/directories-storage-service';

type TOption = {
  label: string;
  value: number;
};

type TDeps = {
  fieldId: string[];
  attrs: string[];
  outer: string[];
};

export const getDynamicJoinDependencies = (refQuery: TRefQuery): TDeps => {
  const dependencies: TDeps = {
    fieldId: [],
    attrs: [],
    outer: [],
  };

  const stringifiedRefQuery = JSON.stringify(refQuery);

  const values = Array.from(stringifiedRefQuery.matchAll(REF_QUERY_REG_EXP));

  for (const value of values) {
    const type = value[1];
    const valueKey = value[2];
    switch (type) {
      // Values are obtained from controls
      case '$': {
        dependencies.attrs.push(`$${valueKey}`);
        break;
      }
      // Values are obtained from directories
      case '%': {
        dependencies.fieldId.push(`%${valueKey}`);
        break;
      }
      //  Values are obtained from outer object of global variables
      case '@': {
        dependencies.outer.push(`@${valueKey}`);
        break;
      }
    }
  }

  return dependencies;
};

type TCreateOptionsFromObjectsFn = (
  objects: TStorageObject<Record<string, unknown>>[],
  attrName: string | null,
  getLabel?: (obj: TStorageObject<Record<string, unknown>>) => string,
) => {
  options: TOption[];
  archivedOptions: TOption[];
  optionsMap: Map<number, TOption>;
  archivedOptionsMap: Map<number, TOption>;
};

const createOptionsFromObjects: TCreateOptionsFromObjectsFn = (objects, attrName, getLabel) => {
  const options: TOption[] = [];
  const archivedOptions: TOption[] = [];
  const optionsMap = new Map<number, TOption>();
  const archivedOptionsMap = new Map<number, TOption>();

  for (const object of objects) {
    const isArchived = object.status === 'DELETED';

    const label = ((): string | null => {
      if (getLabel) {
        return getLabel(object);
      }

      if (attrName) {
        const labelValue = object.data[attrName];

        if (isPrimitive(labelValue)) {
          return labelValue.toString();
        }

        return null;
      }

      return null;
    })();

    if (!label) {
      continue;
    }

    if (isArchived) {
      const option = { label, value: object.id };
      archivedOptions.push(option);
      archivedOptionsMap.set(object.id, option);
    } else {
      const option = { label, value: object.id };
      options.push(option);
      optionsMap.set(object.id, option);
    }
  }

  return { options, archivedOptions, optionsMap, archivedOptionsMap };
};

export class OptionsService {
  private readonly directoriesStorageService: IDirectoriesStorage;
  private readonly notificationsService: NotificationsService;

  @observable private objectSubscriptionDisposers: Record<string, VoidFunction> = {};
  @observable.shallow readonly options: Record<string, TOption[]> = {};
  @observable.shallow readonly archivedOptions: Record<string, TOption[]> = {};
  readonly optionsMap: Record<string, Map<number | string, TOption>> = {};
  readonly archivedOptionsMap: Record<string, Map<number | string, TOption>> = {};

  constructor(notificationsService: NotificationsService, directoriesStorageService: IDirectoriesStorage) {
    this.notificationsService = notificationsService;
    this.directoriesStorageService = directoriesStorageService;
    makeObservable(this);
  }

  @action.bound
  createInitialOptions(controlsViews: TControlView[]): void {
    for (const control of controlsViews) {
      if ('refObjectType' in control) {
        if ('optionsConcatination' in control && !!control.optionsConcatination) {
          const objectType = control.refObjectType;

          if (this.options[objectType]?.length) {
            continue;
          }

          const objects = this.directoriesStorageService.directories[objectType] ?? [];
          const concatinationRule = mapComboboxOptionsConcatinationRule({
            concatinationView: control.optionsConcatination,
          });

          const updateOptions = (objects: TStorageObject[]) => {
            const getLabel = (obj: TStorageObject<Record<string, unknown>>): string => {
              return handleComboboxOptionsConcatinationRule(
                this.directoriesStorageService,
                {
                  ...obj.data,
                  id: obj.id,
                },
                concatinationRule.rule,
              ).value;
            };

            const { options, archivedOptions, optionsMap, archivedOptionsMap } = createOptionsFromObjects(
              objects,
              null,
              getLabel,
            );

            this.setOptions(control.fieldId, options, optionsMap);
            this.setArchivedOptions(control.fieldId, archivedOptions, archivedOptionsMap);
          };

          updateOptions(objects);

          // Subscribe to the list of objects in the desired directory in order to respond to changes in the composition of this list
          const disposer = this.directoriesStorageService.subscribeOnObjectsChange(objectType, updateOptions);

          this.objectSubscriptionDisposers[objectType] = disposer;

          continue;
        }

        if ('refObjectAttr' in control) {
          const objectAttr = control.refObjectAttr;
          const objectType = control.refObjectType;

          if (this.options[objectType]?.length) {
            continue;
          }

          const updateOptions = (objects: TStorageObject[]) => {
            const { options, archivedOptions, optionsMap, archivedOptionsMap } = createOptionsFromObjects(
              objects,
              objectAttr,
            );
            this.setOptions(control.fieldId, options, optionsMap);
            this.setArchivedOptions(control.fieldId, archivedOptions, archivedOptionsMap);
          };

          const objects = this.directoriesStorageService.directories[objectType] ?? [];
          updateOptions(objects);

          // Subscribe to the list of objects in the desired directory in order to respond to changes in the composition of this list
          const disposer = this.directoriesStorageService.subscribeOnObjectsChange(objectType, updateOptions);

          this.objectSubscriptionDisposers[objectType] = disposer;
        }
      }
    }
  }

  @flow.bound
  async *getOptionsByRefQuery(
    controlView: TComboBoxView | TMultiComboBoxView,
    data: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<TOption[]> {
    const refQuery = controlView.refQuery;

    if (!refQuery) {
      console.warn('ref query is not presented');
      return [];
    }

    const options: TOption[] = [];

    try {
      const dependencies = getDynamicJoinDependencies(refQuery);
      const dependenciesObject: Record<string, unknown> = {};

      for (const dep of dependencies.fieldId) {
        const key = dep.slice(1);
        const value = data[key];

        if (hasValue(value)) {
          dependenciesObject[dep] = value;
        }
      }

      // TODO: зачем нам dependencies.attrs?
      for (const dep of dependencies.attrs) {
        const key = dep.slice(1);
        const value = data[key];

        if (hasValue(value)) {
          dependenciesObject[dep] = value;
        }
      }

      const objectsArr = await this.directoriesStorageService.loadObjectsByRefQuery<Record<string, unknown>>(
        refQuery,
        dependenciesObject,
        signal,
      );
      yield;

      if (!objectsArr) {
        return [];
      }

      for (const objects of objectsArr) {
        const mainObject = objects?.[controlView.refObjectType];

        if (!mainObject) {
          continue;
        }

        const value = mainObject.id;

        if ('concatination' in controlView) {
          // TODO: обработать конкатенацию
        }

        const label = (() => {
          if (controlView.optionsConcatination) {
            const concatinationRule = mapComboboxOptionsConcatinationRule({
              concatinationView: controlView.optionsConcatination,
            });
            const getLabel = (obj: TStorageObject<Record<string, unknown>>): string => {
              return handleComboboxOptionsConcatinationRule(
                this.directoriesStorageService,
                {
                  ...obj.data,
                  id: obj.id,
                },
                concatinationRule.rule,
              ).value;
            };

            return getLabel(mainObject);
          }

          return mainObject.data[controlView.refObjectAttr];
        })();

        if (isPrimitive(label)) {
          options.push({
            label: label.toString(),
            value,
          });
        } else {
          console.warn(`invalid label for type: ${controlView.refObjectType}, attr: ${controlView.refObjectAttr}`);
        }
      }

      return options;
    } catch (e) {
      yield;
      console.error(e);
    }

    return options;
  }

  @action.bound
  setOptions(attrName: string, options: TOption[], optionsMap: Map<number, TOption>): void {
    this.options[attrName] = options;
    this.optionsMap[attrName] = optionsMap;
  }

  @action.bound
  setArchivedOptions(attrName: string, options: TOption[], archivedOptionsMap: Map<number, TOption>): void {
    this.archivedOptions[attrName] = options;
    this.archivedOptionsMap[attrName] = archivedOptionsMap;
  }

  init = (): VoidFunction => {
    return () => {
      Object.values(this.objectSubscriptionDisposers).forEach((disposer) => disposer());
    };
  };
}
